import React, { useCallback, useEffect, useState } from "react";
import axios from "../../API/axios";
import CloseSharpIcon from "@mui/icons-material/CloseSharp";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import CandidateReadinessBadges from "../../components/CandidateReadinessBadges";

/**
 * Recommended candidates for a job — company JobApplications / Manage Jobs UX.
 * No gate-status filter (crossed out in product). Min score + search + assign via OfferJob.
 */
const RecommendedCandidatesModal = ({ show, jobId, jobTitle, onClose }) => {
  const { t } = useTranslation();
  const sessionId = localStorage.getItem("sessionId");
  const [search, setSearch] = useState("");
  const [minScore, setMinScore] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [title, setTitle] = useState(jobTitle || "");
  const [candidates, setCandidates] = useState([]);
  const [selected, setSelected] = useState({});
  const [loading, setLoading] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [readinessFilter, setReadinessFilter] = useState("all");

  const load = useCallback(
    async (pageNumber = 1, refreshRanking = false) => {
      if (!jobId) return;
      setLoading(true);
      setError("");
      setMessage("");
      try {
        const res = await axios.post("JobApplication/RecommendedCandidates", {
          deviceType: "Web",
          sessionId,
          jobId,
          search: search || null,
          minScore: Number(minScore) || 0,
          pageNumber,
          pageSize,
          refreshRanking,
          readinessFilter: readinessFilter === "all" ? null : readinessFilter,
        });
        const data = res?.data?.responseData || {};
        setTitle(data.jobTitle || data.JobTitle || jobTitle || "");
        setCandidates(data.candidates || data.Candidates || []);
        setPage(data.pageNumber || data.PageNumber || pageNumber);
        setTotalPages(data.totalPages || data.TotalPages || 0);
        setMessage(res?.data?.message || "");
        setSelected({});
      } catch (e) {
        setError(e?.response?.data?.message || "Failed to load candidates.");
        setCandidates([]);
      } finally {
        setLoading(false);
      }
    },
    [jobId, jobTitle, minScore, pageSize, search, sessionId, readinessFilter]
  );

  useEffect(() => {
    if (show && jobId) {
      load(1, false);
    }
  }, [show, jobId]); // eslint-disable-line react-hooks/exhaustive-deps

  const selectedUids = Object.keys(selected).filter((k) => selected[k]);

  const toggle = (uid) => setSelected((prev) => ({ ...prev, [uid]: !prev[uid] }));

  const assignSelected = async () => {
    if (!selectedUids.length) return;
    setAssigning(true);
    setError("");
    let ok = 0;
    let fail = 0;
    for (const userUid of selectedUids) {
      try {
        await axios.post("JobAction/OfferJob", {
          deviceType: "Web",
          sessionId,
          jobId,
          userUid,
        });
        ok += 1;
      } catch {
        fail += 1;
      }
    }
    setAssigning(false);
    setMessage(`Assigned ${ok}${fail ? `, failed ${fail}` : ""}.`);
    load(page, true);
  };

  if (!show) return null;

  return (
    <div className="modal fade show" style={{ display: "block", background: "rgba(0,0,0,.45)" }} tabIndex="-1">
      <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable" role="document">
        <div className="modal-content p-3">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <h5 className="mb-0">Recommended candidates</h5>
            <CloseSharpIcon className="pointer" onClick={onClose} />
          </div>

          <input
            className="form-control mb-3 fw-semibold"
            value={title}
            readOnly
            placeholder="Job title"
          />

          <div className="border rounded p-2 mb-3">
            <div className="fw-semibold mb-2">Candidate search</div>
            <div className="row g-2 align-items-end">
              <div className="col-md-4">
                <label className="small text-muted">Search</label>
                <input
                  className="form-control"
                  placeholder="Candidate id or name"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
              <div className="col-md-2">
                <label className="small text-muted">Min score (%)</label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  className="form-control"
                  value={minScore}
                  onChange={(e) => setMinScore(e.target.value)}
                />
              </div>
              <div className="col-md-2">
                <label className="small text-muted">Page size</label>
                <select className="form-select" value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))}>
                  {[10, 20, 50].map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-md-4">
                <label className="small text-muted">{t("Recruiter.Readiness")}</label>
                <select
                  className="form-select"
                  value={readinessFilter}
                  onChange={(e) => setReadinessFilter(e.target.value)}
                >
                  <option value="all">{t("Recruiter.AllCandidates")}</option>
                  <option value="kyc">{t("Recruiter.KycDone")}</option>
                  <option value="unverified">{t("Recruiter.KycPending")}</option>
                  <option value="dueSoon">{t("Recruiter.UpdateDueSoon")}</option>
                  <option value="overdue">{t("Recruiter.OneYearOverdue")}</option>
                  <option value="incomplete">{t("Recruiter.IncompleteProfile")}</option>
                </select>
              </div>
              <div className="col-md-12 d-flex gap-2 mt-2">
                <button type="button" className="mainBtn1" onClick={() => load(1, false)} disabled={loading}>
                  {t("Recruiter.Apply")}
                </button>
                <button
                  type="button"
                  className="wlj-ghost-btn"
                  onClick={() => {
                    setSearch("");
                    setMinScore(0);
                    setPageSize(20);
                    setReadinessFilter("all");
                    setTimeout(() => load(1, false), 0);
                  }}
                >
                  {t("Recruiter.Reset")}
                </button>
              </div>
            </div>
          </div>

          <div className="d-flex justify-content-between align-items-center mb-2">
            <button
              type="button"
              className="btn btn-outline-success btn-sm"
              disabled={!selectedUids.length || assigning}
              onClick={assignSelected}
            >
              ASSIGN SELECTED ({selectedUids.length})
            </button>
            <button type="button" className="btn btn-outline-primary btn-sm" disabled={loading} onClick={() => load(page, true)}>
              REFRESH RANKING
            </button>
          </div>

          {loading && <p>Loading…</p>}
          {error && <p className="text-danger">{error}</p>}
          {!loading && !candidates.length && (
            <p className="text-muted">{message || "No candidates found for the selected filters."}</p>
          )}

          <div className="list-group mb-3">
            {candidates.map((c) => {
              const uid = c.userUid || c.UserUid;
              const score = c.matchScore ?? c.MatchScore ?? 0;
              return (
                <div key={uid} className="list-group-item d-flex align-items-center gap-2">
                  <input type="checkbox" checked={!!selected[uid]} onChange={() => toggle(uid)} disabled={c.alreadyApplied || c.AlreadyApplied} />
                  <div className="flex-grow-1">
                    <div className="d-flex justify-content-between">
                      <Link to={`/Profile/${uid}`} className="fw-semibold">
                        #{c.rank ?? c.Rank} {c.fullName || c.FullName}
                      </Link>
                      <span className="wlj-status-chip wlj-status-chip--match">{Math.round(score)}% {t("Recruiter.Match")}</span>
                    </div>
                    <div className="small text-muted">{c.headline || c.Headline || uid}</div>
                    {(c.explain || c.Explain) && (
                      <div className="small">{c.explain || c.Explain}</div>
                    )}
                    <CandidateReadinessBadges candidate={c} />
                    {(c.rankingFrozen || c.RankingFrozen) && (
                      <span className="wlj-status-chip wlj-status-chip--muted">{t("Recruiter.RankingFrozen")}</span>
                    )}
                    {(c.alreadyApplied || c.AlreadyApplied) && (
                      <span className="wlj-status-chip wlj-status-chip--info">{t("Recruiter.AlreadyApplied")}</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="d-flex justify-content-between align-items-center">
            <button type="button" className="btn btn-light btn-sm" disabled={page <= 1 || loading} onClick={() => load(page - 1, false)}>
              PREVIOUS
            </button>
            <span className="small text-muted">
              Page {totalPages === 0 ? 0 : page} of {totalPages}
            </span>
            <button
              type="button"
              className="btn btn-light btn-sm"
              disabled={page >= totalPages || loading}
              onClick={() => load(page + 1, false)}
            >
              NEXT
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RecommendedCandidatesModal;
